import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  X,
  Play,
  Trash2,
  Edit3,
  Activity,
  Clock,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Target,
  Upload,
  Camera,
  Sparkles,
  BarChart3,
  FileText,
} from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { toast } from "sonner";
import useInstanceStore from "../stores/instanceStore";
import { getAuthHeader } from "../services/authToken";

const InstanceDashboard = ({ instance, onClose, readOnly = false }) => {
  const { t } = useTranslation();
  const {
    selectedInstanceId,
    setSelectedInstanceId,
    instances,
    updateInstance,
    removeInstance,
  } = useInstanceStore();
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [isRenaming, setIsRenaming] = useState(false);
  const [newName, setNewName] = useState(
    instance?.title || instance?.name || ""
  );
  const [deleteConfirmation, setDeleteConfirmation] = useState(false);
  const [errorLogs, setErrorLogs] = useState([]);
  const [activationError, setActivationError] = useState(null);
  const [resumeModalOpen, setResumeModalOpen] = useState(false);
  const [completionData, setCompletionData] = useState(null);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isSavingThumbnail, setIsSavingThumbnail] = useState(false);
  const fileInputRef = useRef(null);

  const isActive = selectedInstanceId === instance?.id;
  const isReadOnlyMode = readOnly || instance?.is_demo;

  useEffect(() => {
    if (isReadOnlyMode) {
      setIsRenaming(false);
      setDeleteConfirmation(false);
    }
  }, [isReadOnlyMode]);

  useEffect(() => {
    if (instance?.id) {
      loadAnalytics();
      loadErrorLogs();
      checkCompletion();
      // Load custom thumbnail from localStorage if it exists
      const thumbnailKey = `instance_thumbnail_${instance.id}`;
      const savedThumbnail = localStorage.getItem(thumbnailKey);
      if (savedThumbnail) {
        setImagePreview(savedThumbnail);
      }
    }
  }, [instance?.id]);

  const checkCompletion = async () => {
    if (!instance?.id || !instance?.document_id) return;

    try {
      const backendUrl =
        process.env.REACT_APP_BACKEND_URL || "http://localhost:8002";
      const response = await fetch(
        `${backendUrl}/api/documents/${instance.document_id}/completion-status?instanceId=${instance.id}`,
        {
          headers: getAuthHeader(),
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (
          data.completion &&
          !data.completion.isComplete &&
          data.completion.missingChapters.length > 0
        ) {
          setCompletionData(data.completion);
        }
      }
    } catch (error) {
      console.error("Error checking completion:", error);
    }
  };

  const loadAnalytics = async () => {
    try {
      const backendUrl =
        process.env.REACT_APP_BACKEND_URL || "http://localhost:8002";

      const response = await fetch(
        `${backendUrl}/api/instances/${instance.id}/analytics`,
        {
          headers: getAuthHeader(),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setAnalytics(data);
      } else {
        setAnalytics({
          posts_count: instance.posts_count || 0,
          last_processed: instance.updated_at || new Date().toISOString(),
          status: instance.status || "ready",
          errors: 0,
          progress: instance.progress || 100,
        });
      }
    } catch (error) {
      console.error("Error loading analytics:", error);
      setAnalytics({
        posts_count: instance.posts_count || 0,
        last_processed: instance.updated_at || new Date().toISOString(),
        status: instance.status || "ready",
        errors: 0,
        progress: instance.progress || 100,
      });
    }
  };

  const loadErrorLogs = async () => {
    try {
      const backendUrl =
        process.env.REACT_APP_BACKEND_URL || "http://localhost:8002";

      const response = await fetch(
        `${backendUrl}/api/instances/${instance.id}/errors`,
        {
          headers: getAuthHeader(),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setErrorLogs(data.errors || []);
      }
    } catch (error) {
      console.error("Error loading error logs:", error);
      setErrorLogs([]);
    }
  };

  const handleSetActive = async () => {
    if (isReadOnlyMode || isActive || loading) return;

    if (isActive || loading) return;

    setLoading(true);
    setActivationError(null);

    try {
      const backendUrl =
        process.env.REACT_APP_BACKEND_URL || "http://localhost:8002";

      const response = await fetch(
        `${backendUrl}/api/instances/${instance.id}/activate`,
        {
          method: "POST",
          headers: {
            ...getAuthHeader(),
            "Content-Type": "application/json",
          },
          body: JSON.stringify({}),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage =
          errorData.message || errorData.error || "Failed to activate instance";
        const errorDetails = {
          message: errorMessage,
          status: response.status,
          timestamp: new Date().toISOString(),
          instanceId: instance.id,
          instanceStatus: instance.status,
        };

        setActivationError(errorDetails);

        // Log detailed error for debugging
        console.error("Instance activation failed:", {
          instanceId: instance.id,
          instanceStatus: instance.status,
          responseStatus: response.status,
          errorData,
        });

        throw new Error(errorMessage);
      }

      setSelectedInstanceId(instance.id);
      toast.success(`Now showing: ${instance.title || instance.name}`);

      // Trigger feed refresh via event
      window.dispatchEvent(
        new CustomEvent("instance-activated", {
          detail: { instanceId: instance.id },
        })
      );
    } catch (error) {
      console.error("Error activating instance:", error);
      toast.error(`Failed to activate instance: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (isReadOnlyMode || loading) return;

    if (!deleteConfirmation) {
      setDeleteConfirmation(true);
      return;
    }

    if (isActive) {
      toast.error(
        "Cannot delete active instance. Please activate another instance first."
      );
      setDeleteConfirmation(false);
      return;
    }

    setLoading(true);
    try {
      const backendUrl =
        process.env.REACT_APP_BACKEND_URL || "http://localhost:8002";

      const response = await fetch(
        `${backendUrl}/api/instances/${instance.id}`,
        {
          method: "DELETE",
          headers: getAuthHeader(),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to delete instance");
      }

      removeInstance(instance.id);
      toast.success("Instance deleted successfully");
      onClose();
    } catch (error) {
      console.error("Error deleting instance:", error);
      toast.error("Failed to delete instance");
    } finally {
      setLoading(false);
      setDeleteConfirmation(false);
    }
  };

  const handleRename = async () => {
    if (isReadOnlyMode) return;

    if (!isRenaming) {
      setIsRenaming(true);
      return;
    }

    if (!newName.trim() || newName === (instance.title || instance.name)) {
      setIsRenaming(false);
      setNewName(instance.title || instance.name);
      return;
    }

    setLoading(true);
    try {
      const backendUrl =
        process.env.REACT_APP_BACKEND_URL || "http://localhost:8002";

      const response = await fetch(
        `${backendUrl}/api/instances/${instance.id}`,
        {
          method: "PATCH",
          headers: {
            ...getAuthHeader(),
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ name: newName.trim() }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to rename instance");
      }

      const updatedInstance = await response.json();
      updateInstance(instance.id, {
        name: newName.trim(),
        title: newName.trim(),
      });
      toast.success("Instance renamed successfully");
      setIsRenaming(false);
    } catch (error) {
      console.error("Error renaming instance:", error);
      toast.error("Failed to rename instance");
      setNewName(instance.title || instance.name);
    } finally {
      setLoading(false);
    }
  };

  const handleResumeGeneration = async () => {
    if (
      isReadOnlyMode ||
      !instance?.id ||
      !instance?.document_id ||
      !completionData
    )
      return;

    setResumeLoading(true);
    try {
      const backendUrl =
        process.env.REACT_APP_BACKEND_URL || "http://localhost:8002";
      const response = await fetch(
        `${backendUrl}/api/documents/${instance.document_id}/resume-generation`,
        {
          method: "POST",
          headers: {
            ...getAuthHeader(),
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            instanceId: instance.id,
            chapterIndices: completionData.missingChapters,
          }),
        }
      );

      if (response.ok) {
        const result = await response.json();
        toast.success(
          `Resumed generation for ${result.queuedChapters} chapters`
        );
        setResumeModalOpen(false);
        setCompletionData(null); // Clear completion data since we've resumed
      } else {
        const error = await response.json();
        throw new Error(error.message || "Failed to resume generation");
      }
    } catch (error) {
      console.error("Error resuming generation:", error);
      toast.error(`Failed to resume generation: ${error.message}`);
    } finally {
      setResumeLoading(false);
    }
  };

  const handleImageUpload = (event) => {
    if (isReadOnlyMode) return;
    const file = event.target.files[0];
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Create canvas for cropping to 1:1 aspect ratio
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          const size = Math.min(img.width, img.height);
          canvas.width = 400; // Fixed size for thumbnail
          canvas.height = 400;

          // Calculate cropping dimensions
          const sx = (img.width - size) / 2;
          const sy = (img.height - size) / 2;

          // Draw cropped image
          ctx.drawImage(img, sx, sy, size, size, 0, 0, 400, 400);

          // Convert to base64
          const croppedImage = canvas.toDataURL("image/jpeg", 0.8);
          setImagePreview(croppedImage);
          setUploadedImage(file);

          // Save to instance
          saveCustomThumbnail(croppedImage);
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const saveCustomThumbnail = async (imageData) => {
    try {
      setIsSavingThumbnail(true);

      // Convert base64 to Blob
      const base64Data = imageData.split(',')[1];
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/jpeg' });

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', blob, `${instance.id}-${Date.now()}.jpg`);

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8002';

      // Upload to Supabase Storage via backend
      const uploadResponse = await fetch(`${backendUrl}/api/instances/${instance.id}/cover-image`, {
        method: 'POST',
        headers: getAuthHeader(),
        body: formData
      });

      if (!uploadResponse.ok) {
        const error = await uploadResponse.json();
        throw new Error(error.error || 'Failed to upload cover image');
      }

      const { coverImageUrl } = await uploadResponse.json();
      console.log('✅ Image uploaded to Supabase Storage:', coverImageUrl);

      // Update instance with cover_image URL in database
      const updateResponse = await fetch(`${backendUrl}/api/instances/${instance.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({ cover_image: coverImageUrl })
      });

      if (!updateResponse.ok) {
        const error = await updateResponse.json();
        throw new Error(error.error || 'Failed to update instance');
      }

      console.log('✅ Database updated with cover_image URL');

      // Update Zustand store with new cover_image
      updateInstance(instance.id, { cover_image: coverImageUrl });

      // Keep localStorage as cache for instant loading
      const thumbnailKey = `instance_thumbnail_${instance.id}`;
      localStorage.setItem(thumbnailKey, imageData);

      toast.success("Cover image updated successfully!");
    } catch (error) {
      console.error("❌ Error updating cover image:", error);
      toast.error(`Failed to update cover image: ${error.message}`);
    } finally {
      setIsSavingThumbnail(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Never";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "ready":
      case "active":
        return "text-emerald-500";
      case "processing":
        return "text-amber-500";
      case "error":
        return "text-rose-500";
      default:
        return "text-slate-500";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "ready":
      case "active":
        return <CheckCircle className="w-4 h-4" />;
      case "processing":
        return <Clock className="w-4 h-4" />;
      case "error":
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <Activity className="w-4 h-4" />;
    }
  };

  if (!instance) return null;

  const progressPercentage = analytics?.progress || instance.progress || 100;
  const isProcessing = instance.status === "processing";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Processing Loading Modal Overlay */}
      {isProcessing && (
        <div className="absolute inset-0 flex items-center justify-center z-60">
          <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-3xl p-8 text-center max-w-sm mx-4 shadow-2xl">
            <div className="relative w-24 h-24 mx-auto mb-6">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 to-purple-600/20 rounded-full blur-lg" />
              <svg className="w-24 h-24 transform -rotate-90 relative z-10">
                <circle
                  cx="48"
                  cy="48"
                  r="40"
                  stroke="rgba(255,255,255,0.2)"
                  strokeWidth="6"
                  fill="none"
                />
                <circle
                  cx="48"
                  cy="48"
                  r="40"
                  stroke="url(#processing-gradient)"
                  strokeWidth="6"
                  fill="none"
                  strokeDasharray={`${2 * Math.PI * 40}`}
                  strokeDashoffset={`${
                    2 * Math.PI * 40 * (1 - progressPercentage / 100)
                  }`}
                  className="transition-all duration-500 ease-out"
                  strokeLinecap="round"
                />
                <defs>
                  <linearGradient
                    id="processing-gradient"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="100%"
                  >
                    <stop offset="0%" stopColor="#3B82F6" />
                    <stop offset="100%" stopColor="#8B5CF6" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex items-center justify-center z-20">
                <span className="text-white text-lg font-bold">
                  {Math.round(progressPercentage)}%
                </span>
              </div>
            </div>
            <h3 className="text-white text-xl font-semibold mb-3">
              {t('instance_dashboard.processing_content_title')}
            </h3>
            <p className="text-white/80 text-sm mb-6">
              {t('instance_dashboard.processing_content_description')}
            </p>
            <div className="text-blue-200 text-sm font-medium">
              {progressPercentage < 25
                ? t('instance_dashboard.processing_step_1')
                : progressPercentage < 60
                ? t('instance_dashboard.processing_step_2')
                : progressPercentage < 85
                ? t('instance_dashboard.processing_step_3')
                : t('instance_dashboard.processing_step_4')}
            </div>
          </div>
        </div>
      )}

      {/* Dashboard Modal - Modern Design */}
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden animate-scale-in backdrop-blur-lg border border-white/20">
        {/* Header with Gradient */}
        <div className="relative h-48 bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 p-6">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.15)_1px,transparent_0)] bg-[length:20px_20px]" />
          </div>

          <button
            onClick={onClose}
            className=" z-50 absolute top-5 right-5 w-9 h-9 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-all duration-200 active:scale-95 group"
          >
            <X className="w-4 h-4 group-hover:scale-110 transition-transform" />
          </button>

          <div className="flex flex-col justify-end h-full relative z-10">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                {isRenaming ? (
                  <input
                    type="text"
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onBlur={() => handleRename()}
                    onKeyPress={(e) => e.key === "Enter" && handleRename()}
                    className="bg-white/15 backdrop-blur-sm rounded-xl px-4 py-3 text-white placeholder-white/60 w-full border border-white/20 focus:border-white/40 focus:outline-none focus:ring-2 focus:ring-white/20 transition-all"
                    autoFocus
                  />
                ) : (
                  <h2 className="text-2xl font-bold text-white mb-2 leading-tight pr-8">
                    {instance.title || instance.name}
                  </h2>
                )}
              </div>
            </div>

            {isActive && (
              <div className="flex items-center gap-2 bg-emerald-500/20 backdrop-blur-sm px-2 py-1 rounded-full border border-emerald-500/30 w-fit">
                <div className="w-1 h-1 bg-emerald-400 rounded-full animate-pulse" />
                <span className="text-emerald-100 text-xs font-medium">
                  {t('instance_dashboard.currently_active')}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Progress Ring - Centered Overlap */}
        <div className="relative -mt-16 flex justify-center mb-8 ml-0">
          <div className="relative">
            {/* Background Glow */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full blur-lg opacity-20 scale-110" />

            <div className="relative w-28 h-28 bg-white rounded-full shadow-xl p-4 border border-slate-100">
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-2xl font-bold text-slate-900">
                  {progressPercentage}%
                </span>
                <span className="text-xs text-slate-500 font-medium">
                  {t('common.complete')}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Read-only Notice */}
        {isReadOnlyMode && (
          <div className="mx-6 mb-6 p-4 bg-blue-50/80 backdrop-blur-sm border border-blue-200 rounded-2xl text-sm text-blue-800">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4" />
              <span className="font-semibold">{t('instance_dashboard.demo_instance_title')}</span>
            </div>
            <p>{t('instance_dashboard.demo_instance_description')}</p>
          </div>
        )}

        {/* Custom Thumbnail Section */}
        {!isReadOnlyMode && (
          <div className="px-6 mb-6">
            <div className="bg-slate-50/80 backdrop-blur-sm rounded-2xl p-5 border border-slate-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                  <Camera className="w-4 h-4" />
                  {t('instance_dashboard.custom_thumbnail_title')}
                </h3>
                <div className="w-6 h-6 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <Upload className="w-3 h-3 text-white" />
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="relative">
                  {imagePreview || instance?.custom_thumbnail ? (
                    <img
                      src={imagePreview || instance.custom_thumbnail}
                      alt="Instance thumbnail"
                      className="w-20 h-20 rounded-2xl object-cover border-2 border-white shadow-lg"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center border-2 border-white shadow-lg">
                      <Camera className="w-6 h-6 text-slate-500" />
                    </div>
                  )}
                </div>
                <div className="flex-1">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full px-4 py-3 bg-white text-slate-700 text-sm font-semibold rounded-xl border border-slate-300 hover:border-slate-400 hover:bg-slate-50 transition-all duration-200 active:scale-95 shadow-sm"
                  >
                    {t('instance_dashboard.choose_image')}
                  </button>
                  <p className="text-xs text-slate-500 mt-2 text-center">
                    {t('instance_dashboard.square_images_tip')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="px-6 mb-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-5 border border-blue-100">
              <div className="flex items-center gap-2 mb-3">
                <FileText className="w-4 h-4 text-blue-600" />
                <span className="text-xs font-semibold text-blue-900 uppercase tracking-wide">
                  {t('instance_dashboard.content_items_title')}
                </span>
              </div>
              <div className="text-2xl font-bold text-slate-900 mb-1">
                {analytics?.posts_count || instance?.posts_count || 0}
              </div>
              <div className="text-xs text-blue-700 font-medium">
                {t('instance_dashboard.generated_posts')}
              </div>
            </div>

            <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl p-5 border border-slate-200">
              <div className="flex items-center gap-2 mb-3">
                <BarChart3 className="w-4 h-4 text-slate-600" />
                <span className="text-xs font-semibold text-slate-900 uppercase tracking-wide">
                  {t('instance_dashboard.status_title')}
                </span>
              </div>
              <div className="flex items-center gap-2 mb-1">
                {getStatusIcon(analytics?.status || instance.status)}
                <span
                  className={`text-sm font-semibold capitalize ${getStatusColor(
                    analytics?.status || instance.status
                  )}`}
                >
                  {analytics?.status || instance.status || t('common.unknown')}
                </span>
              </div>
              <div className="text-xs text-slate-600 font-medium">
                {formatDate(analytics?.last_processed)}
              </div>
            </div>
          </div>
        </div>

        {/* Resume Generation Alert */}
        {!isReadOnlyMode && completionData && (
          <div className="mx-6 mb-6 p-4 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                <Target className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h4 className="text-sm font-semibold text-amber-900">
                  {t('instance_dashboard.continue_processing_title')}
                </h4>
                <p className="text-xs text-amber-700">
                  {t('instance_dashboard.chapters_remaining', { count: completionData.missingChapters.length })}
                </p>
              </div>
            </div>
            <Button
              onClick={() => setResumeModalOpen(true)}
              size="sm"
              className="w-full bg-amber-500 hover:bg-amber-600 text-white font-semibold py-3 rounded-xl transition-all duration-200 active:scale-95"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              {t('instance_dashboard.resume_generation_button')}
            </Button>
          </div>
        )}

        {/* Control Panel */}
        <div className="px-6 pb-8">
          {isReadOnlyMode ? (
            <div className="p-5 bg-slate-50/80 backdrop-blur-sm border border-slate-200 rounded-2xl text-sm text-slate-700">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-slate-600" />
                <span className="font-semibold">{t('instance_dashboard.demo_mode_active_title')}</span>
              </div>
              <p>{t('instance_dashboard.demo_mode_active_description')}</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <button
                  onClick={handleSetActive}
                  disabled={
                    isActive || loading || instance.status === "processing"
                  }
                  className={`
                    group relative flex flex-row items-center justify-center gap-2 px-4 rounded-md transition-all duration-200 border
                    ${
                      isActive
                        ? "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed"
                        : instance.status === "processing"
                        ? "bg-amber-100 text-amber-400 border-amber-200 cursor-not-allowed"
                        : "bg-white text-slate-700 border-slate-300 hover:border-blue-500 hover:bg-blue-50 hover:text-blue-600 active:scale-95 shadow-sm"
                    }
                  `}
                  title={
                    instance.status === "processing"
                      ? t('instance_dashboard.processing_instance_tooltip')
                      : isActive
                      ? t('instance_dashboard.already_active_tooltip')
                      : t('instance_dashboard.set_active_instance_tooltip')
                  }
                >
                  <span className="text-sm font-semibold">
                    {instance.status === "processing"
                      ? t('common.processing')
                      : t('common.activate')}
                  </span>
                  <div
                    className={`p-1 rounded-full flex items-center justify-center  transition-colors ${
                      isActive
                        ? "bg-slate-200"
                        : instance.status === "processing"
                        ? "bg-amber-200"
                        : "bg-blue-100 group-hover:bg-blue-200"
                    }`}
                  >
                    <Play
                      className={`w-3 h-3 ${
                        isActive
                          ? "text-slate-400"
                          : instance.status === "processing"
                          ? "text-amber-400"
                          : "text-blue-600 group-hover:text-blue-700"
                      }`}
                    />
                  </div>
                </button>

                <button
                  onClick={
                    deleteConfirmation
                      ? handleDelete
                      : () => setDeleteConfirmation(true)
                  }
                  disabled={loading}
                  className={`
                    group relative flex flex-row gap-2 items-center justify-center px-4 rounded-md transition-all duration-200 border
                    ${
                      deleteConfirmation
                        ? "bg-rose-500 text-white border-rose-500 hover:bg-rose-600 hover:border-rose-600 active:scale-95 shadow-sm"
                        : "bg-white text-slate-700 border-slate-300 hover:border-rose-500 hover:bg-rose-50 hover:text-rose-600 active:scale-95 shadow-sm"
                    }
                  `}
                >
                  <span className="text-sm font-semibold">
                    {deleteConfirmation ? t('common.confirm') : t('common.delete')}
                  </span>

                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                      deleteConfirmation
                        ? "bg-rose-600"
                        : "bg-rose-100 group-hover:bg-rose-200"
                    }`}
                  >
                    <Trash2
                      className={`w-4 h-4 ${
                        deleteConfirmation
                          ? "text-white"
                          : "text-rose-600 group-hover:text-rose-700"
                      }`}
                    />
                  </div>
                </button>

                <button
                  onClick={() => !isRenaming && setIsRenaming(true)}
                  disabled={loading || isRenaming}
                  className="group flex flex-row items-center gap-2 justify-center px-4 bg-white text-slate-700 border rounded-md hover:border-slate-400 hover:bg-slate-50 hover:text-slate-800 transition-all duration-200 active:scale-95 shadow-sm"
                >
                  <span className="text-sm font-semibold">{t('common.rename')}</span>

                  <div className="w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center group-hover:bg-slate-200 transition-colors">
                    <Edit3 className="w-4 h-4 text-slate-600 group-hover:text-slate-700" />
                  </div>
                </button>
              </div>

              {deleteConfirmation && (
                <div className="p-4 bg-rose-50 border border-rose-200 rounded-md animate-fade-in">
                  <p className="text-sm text-rose-800 text-center font-medium mb-3">
                    {t('instance_dashboard.delete_confirmation_message')}
                  </p>
                  <button
                    onClick={() => setDeleteConfirmation(false)}
                    className="w-full py-2 text-sm text-rose-600 hover:text-rose-800 font-semibold transition-colors"
                  >
                    {t('instance_dashboard.cancel_deletion')}
                  </button>
                </div>
              )}
            </>
          )}

          {/* Error and Status Sections */}
          <div className="space-y-3">
            {analytics?.errors > 0 && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl">
                <div className="flex items-center gap-2 mb-1">
                  <AlertCircle className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-semibold text-amber-800">
                    {t('instance_dashboard.processing_issues_title')}
                  </span>
                </div>
                <p className="text-xs text-amber-700">
                  {t('instance_dashboard.processing_issues_description', { count: analytics.errors })}
                </p>
              </div>
            )}

            {activationError && (
              <div className="p-4 bg-rose-50 border border-rose-200 rounded-2xl">
                <div className="flex items-start gap-3 mb-2">
                  <AlertCircle className="w-5 h-5 text-rose-500 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-rose-800 mb-1">
                      {t('instance_dashboard.activation_failed_title')}
                    </p>
                    <p className="text-xs text-rose-700">
                      {activationError.message}
                    </p>
                  </div>
                </div>
                <div className="text-xs text-rose-600 space-y-1">
                  <p>{t('common.status')}: {activationError.status}</p>
                  <p>
                    {t('common.time')}:{" "}
                    {new Date(activationError.timestamp).toLocaleTimeString()}
                  </p>
                </div>
              </div>
            )}

            {errorLogs.length > 0 && (
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl">
                <div className="flex items-center gap-2 mb-3">
                  <Activity className="w-4 h-4 text-slate-600" />
                  <p className="text-sm font-semibold text-slate-800">
                    {t('instance_dashboard.recent_errors_title')}
                  </p>
                </div>
                <div className="space-y-3 max-h-32 overflow-y-auto">
                  {errorLogs.slice(0, 3).map((error, index) => (
                    <div
                      key={index}
                      className="text-xs border-l-2 border-rose-300 pl-3"
                    >
                      <p className="text-slate-700 font-medium">
                        {error.type || t('common.processing_error')}
                      </p>
                      <p className="text-slate-600 mt-1">{error.message}</p>
                      <p className="text-slate-500 text-xs mt-1">
                        {new Date(error.timestamp).toLocaleString()}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Resume Generation Modal */}
      {!isReadOnlyMode && (
        <Dialog open={resumeModalOpen} onOpenChange={setResumeModalOpen}>
          <DialogContent className="max-w-md rounded-3xl border-0 shadow-2xl p-0 overflow-hidden">
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-8 text-center">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-amber-100 rounded-2xl flex items-center justify-center shadow-lg">
                  <Target className="w-8 h-8 text-amber-600" />
                </div>
              </div>
              <DialogTitle className="text-2xl font-bold text-slate-900 mb-3">
                {t('instance_dashboard.continue_processing_title')}
              </DialogTitle>
              <DialogDescription className="text-base text-slate-700">
                {completionData ? (
                  <>
                    {t('instance_dashboard.complete_remaining')}{" "}
                    <span className="font-bold text-amber-600">
                      {t('instance_dashboard.chapters_remaining', { count: completionData.missingChapters.length })}
                    </span>{" "}
                    {t('instance_dashboard.to_finish_processing')}
                  </>
                ) : (
                  t('instance_dashboard.some_chapters_need_processing')
                )}
              </DialogDescription>
            </div>

            {completionData && (
              <div className="p-6 bg-white border-b border-slate-200">
                <div className="flex items-center justify-between text-sm mb-3">
                  <span className="text-slate-600 font-medium">{t('common.progress')}</span>
                  <span className="font-bold text-slate-900">
                    {completionData.completedChapters}/
                    {completionData.totalExpected}
                  </span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-amber-500 to-orange-500 h-3 rounded-full transition-all duration-500 ease-out"
                    style={{
                      width: `${
                        (completionData.completedChapters /
                          completionData.totalExpected) *
                        100
                      }%`,
                    }}
                  />
                </div>
                <div className="flex items-center justify-between text-xs text-slate-500 mt-2">
                  <span className="font-medium">
                    {t('instance_dashboard.chapters_remaining', { count: completionData.missingChapters.length })}
                  </span>
                  <span>~{completionData.missingChapters.length * 2} {t('common.min')}</span>
                </div>
              </div>
            )}

            <DialogFooter className="flex gap-3 p-6 bg-slate-50">
              <Button
                variant="outline"
                onClick={() => setResumeModalOpen(false)}
                disabled={resumeLoading}
                className="flex-1 py-3 rounded-xl border-slate-300 text-slate-700 hover:bg-white font-semibold"
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleResumeGeneration}
                disabled={resumeLoading}
                className="flex-1 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold shadow-lg transition-all duration-200"
              >
                {resumeLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    {t('common.starting')}
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    {t('instance_dashboard.resume_processing_button')}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default InstanceDashboard;
